
<!--
* An image upload wrapper component that wraps the image upload,
* select field and caption input
*
* @param {string} uploadFolder - The folder to upload the image to
* @param {string} initialImageSrc - Url of the image to initially display.
* @param {string} defaultImageSrc - Url of the default image if image hasn't been uploaded
* @param {string} caption - Caption to display under thumbnail if showSelect is false
* @param {boolean} showControls - Whether to show image controls
* @param {boolean} showSelect - Whether to show the select field
* @param {number} selectVal - Index of selected value in select field
* @param {string} selectPlaceholder - The placeholder text for select field
* @param {Object[]} selectOptions - Array of objects for options in select field
* @param {string} selectDisplayKey - The key in select option objects
*                                    to get display value for each option
* @param {File} file - Image to upload on creation of component
*
* @fires MsImageUploadWrapper#imageUploadSuccess
* @fires MsImageUploadWrapper#update:selectVal
* @fires MsImageUploadWrapper#imageDeleteClick
-->

<!--
* Emits an image upload success event.
*
* @event MsImageUploadWrapper#imageUploadSuccess
-->

<!--
* Emits an update event for selectVal prop.
* @see https://vuejs.org/v2/guide/components-custom-events.html#sync-Modifier
*
* @event MsImageUploadWrapper#update:selectVal
-->

<!--
* Emits a image delete click event.
*
* @event MsImageUploadWrapper#imageDeleteClick
-->

<template>
  <div class="image_upload_wrapper">
    <!-- image upload -->
    <ms-image-upload
      :imageUrl="imageUrl"
      :defaultImageSrc="defaultImageSrc"
      :noImageSrc="noImageSrc"
      :showControls="showControls"
      :file="file"
      :uploadFolder="uploadFolder"
      @uploadSuccess="emitImageUploadSuccess"
      @deleteClick="emitImageDelete"/>

    <!-- Caption -->
    <div
      class="form__image_caption"
      v-if="!showSelect">{{ caption }}</div>

    <!-- Select -->
    <appSelect
      class="select is-fullwidth"
      :options="selectOptions"
      :placeholder="selectPlaceholder"
      :display-key="selectDisplayKey"
      v-model="selectedValue"
      v-if="showSelect"/>
  </div>
</template>

<script>
  import appSettings from '@/app-settings.json'
  import MsImageUpload from '@/components/ms-image-upload'
  import AppSelect from '@/components/app-select'

  export default {
    name: 'MsImageUploadWrapper',
    components: {
      MsImageUpload,
      AppSelect
    },

    data: function () {
      return {
        noImageSrc: appSettings.noImagePlaceholder
      }
    },

    props: {
      uploadFolder: {
        type: String,
        required: true
      },
      imageUrl: {
        type: String,
        default: null
      },

      defaultImageSrc: {
        type: String,
        default: null
      },

      caption: {
        type: String
      },

      showSelect: {
        type: Boolean
      },

      selectVal: {
        type: Number
      },

      selectPlaceholder: {
        type: String
      },

      selectDisplayKey: {
        type: String
      },

      selectOptions: {
        type: Array
      },

      showControls: {
        type: Boolean,
        default: false
      },

      file: {
        type: File
      }
    },

    computed: {
      selectedValue: {
        get () {
          return this.selectVal
        },

        set (value) {
          const val = value !== null ? value.index : value
          this.$emit('update:selectVal', val)
        }
      }
    },

    methods: {
      /**
       * Emits image upload success event
       *
       * @param {string} url - Url of successfully uploaded image
       */
      emitImageUploadSuccess: function (url) {
        this.$emit('imageUploadSuccess', url)
      },

      /**
       * Emit image delete event
       */
      emitImageDelete: function () {
        this.$emit('imageDeleteClick')
      }
    }
  }
</script>


<style lang="scss" scoped>
  @import "../assets/sass/shared.scss";

  .form__image_caption {
    display: block;
    width: 100%;
    background: $secondary-brand-color;
    padding: $dims-spacing-small;
    border: $default-border-style;
    border-top: 0;
    font-size: $medium-font-size;
  }

  .select.is-fullwidth {
    height: auto;

    ::v-deep .select {
      height: auto;

      select {
        border: $default-border-style;
        border-top: 0;
        height: auto;
      }
    }
  }
</style>
